/**
 * Card primitive.
 *
 * A raised container for grouped content. Feature screens use it for list
 * tiles, settings panels, and modal bodies.
 *
 * All values resolve from tokens (CP5). `padding` + `shadow` are prop-driven
 * so callers can tune density without redefining the rest of the shape.
 *
 * Legacy equivalent: ad-hoc `.card`, `.settings-card`, `.offer-card` classes
 * in `frontend/src/App.css`. This primitive is their structural union.
 */

import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';
export type CardShadow = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: CardPadding;
  shadow?: CardShadow;
  children?: ReactNode;
}

const paddingToken: Record<CardPadding, string | undefined> = {
  none: undefined,
  sm: 'var(--space-3)',
  md: 'var(--space-4)',
  lg: 'var(--space-6)',
};

const shadowToken: Record<CardShadow, string | undefined> = {
  none: undefined,
  sm: 'var(--shadow-sm)',
  md: 'var(--shadow-md)',
  lg: 'var(--shadow-lg)',
};

export function Card({
  padding = 'md',
  shadow = 'sm',
  style,
  children,
  ...rest
}: CardProps): JSX.Element {
  const composed: CSSProperties = {
    backgroundColor: 'var(--color-bg-raised)',
    color: 'var(--color-text-primary)',
    borderRadius: 'var(--radius-lg)',
    padding: paddingToken[padding],
    boxShadow: shadowToken[shadow],
    ...style,
  };
  return (
    <div
      {...rest}
      data-padding={padding}
      data-shadow={shadow}
      style={composed}
    >
      {children}
    </div>
  );
}
